/**
 * Downscale an oversized image so the stored blob is always within the chat
 * model's per-image pixel limit.
 *
 * Anthropic hard-rejects any image whose width or height exceeds 8000 px,
 * failing the entire chat turn (ADO-1443) — full-resolution phone panoramas and
 * DSLR exports trip this. Resizing once at upload time, before the blob is
 * persisted, means every consumer (chat -> Claude, Firefly) reads an
 * already-safe image and there is no per-turn fetch/decode/resize.
 *
 * 2000 px per edge sits well under the 8000-px single-image cap and also covers
 * Anthropic's stricter limit when a request carries more than 20 images. Images
 * already within the cap pass through byte-identical, so normal-sized assets
 * (logos, IG posts) are never re-encoded. Any decode/encode failure returns the
 * original file unchanged — resizing must never be the thing that blocks an
 * upload.
 */

export const CHAT_IMAGE_MAX_EDGE = 2000;

const RESIZABLE_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

export async function downscaleImageToMaxEdge(
	file: File,
	maxEdge: number = CHAT_IMAGE_MAX_EDGE,
): Promise<File> {
	// Only raster JPEG/PNG are re-encodable here; videos, CSVs, and anything
	// else (the stored blob is always JPEG/PNG once HEIC is transcoded) pass
	// through untouched.
	if (!RESIZABLE_MIME_TYPES.has(file.type)) {
		return file;
	}

	try {
		const bitmap = await createImageBitmap(file);
		const { width, height } = bitmap;
		const longest = Math.max(width, height);
		if (longest <= maxEdge) {
			bitmap.close();
			return file;
		}

		const scale = maxEdge / longest;
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(width * scale);
		canvas.height = Math.round(height * scale);

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			bitmap.close();
			return file;
		}
		ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close();

		const blob = await new Promise<Blob | null>((resolve) => {
			canvas.toBlob(
				resolve,
				file.type,
				file.type === 'image/jpeg' ? 0.92 : undefined,
			);
		});
		if (!blob) {
			return file;
		}

		return new File([blob], file.name, {
			type: file.type,
			lastModified: file.lastModified ?? Date.now(),
		});
	} catch {
		// Best-effort: a decode/encode failure leaves the original file in place
		// (no worse than today). The upload path then proceeds as before.
		return file;
	}
}
